import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('karetek_token');
      const savedUser = localStorage.getItem('karetek_user');
      
      if (token && savedUser) {
        try {
          const userData = await authAPI.getMe();
          setUser(userData);
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('karetek_token');
          localStorage.removeItem('karetek_user');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      localStorage.setItem('karetek_token', response.token);
      localStorage.setItem('karetek_user', JSON.stringify(response.user));
      setUser(response.user);
      return response;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.register(userData);
      localStorage.setItem('karetek_token', response.token);
      localStorage.setItem('karetek_user', JSON.stringify(response.user));
      setUser(response.user);
      return response;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('karetek_token');
    localStorage.removeItem('karetek_user');
    setUser(null);
  }, []);

  const loginWithToken = useCallback(async (token) => {
    setLoading(true);
    setError(null);
    try {
      localStorage.setItem('karetek_token', token);
      const userData = await authAPI.getMe();
      localStorage.setItem('karetek_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      localStorage.removeItem('karetek_token');
      localStorage.removeItem('karetek_user');
      const errorMessage = err.response?.data?.error || 'OAuth login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    loginWithToken,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
